import { apiClient } from '@/features/shared/api/client';
import type { StudentAuthUser } from '../store/authStore';
import {
  normalizePasskeyCreationOptions,
  normalizePasskeyRequestOptions,
  serializePasskeyCredential,
} from '../utils/passkey';

export interface CaptchaChallenge {
  challengeId: string;
  provider: string;
  ticket: string;
  expiresInSeconds: number;
}

export interface SmsCodeResponse {
  cooldownSeconds: number;
  expiresInSeconds: number;
  devCode?: string | null;
}

export interface StudentLoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresInSeconds: number;
  user: StudentAuthUser;
}

export interface PublicRegistrationSettings {
  enabled: boolean;
  defaultPracticeMode: 'practice' | 'mock';
  showPracticeModeSwitch: boolean;
  allowPracticeModeSwitch: boolean;
  showQuestionDifficulty: boolean;
  showHighFrequencyQuestionBadge: boolean;
  showNewQuestionBadge: boolean;
  newQuestionWindowDays: number;
  questionBadgeStyle: 'soft' | 'outline';
  showQuestionNavigation: boolean;
  allowPhoneLogin: boolean;
  allowEmailLogin: boolean;
  allowPasswordLogin: boolean;
  allowPasskeyLogin: boolean;
  requireDisplayName: boolean;
  requireContactEmail: boolean;
  requireEmailVerification: boolean;
  emailCodeExpireMinutes: number;
  allowedEmailDomains: string[];
  requireInviteCode: boolean;
  captchaProvider: string;
  captchaSiteKey?: string | null;
  captchaAction?: string | null;
  defaultTimezone: string;
  onboardingHintText?: string | null;
}

export interface StudentProfile {
  userId: string;
  phoneMasked?: string | null;
  displayName: string;
  contactEmail?: string | null;
  avatarUrl?: string | null;
  emailAvatarUrl?: string | null;
  targetScore?: number | null;
  timezone: string;
  preferredPracticeMode: 'practice' | 'mock';
  registrationStatus: string;
  registrationCompleted: boolean;
  emailVerifiedAt?: string | null;
  onboardingCompletedAt?: string | null;
  registeredAt?: string | null;
}

export interface StudentShellSettings {
  capabilities: {
    learning?: boolean;
    fullMock?: boolean;
    leaderboard?: boolean;
  };
  studentUiLanguage: 'zh-CN' | 'en-US';
  practiceUiLanguageIndependent: boolean;
  practiceActionLanguageMode: 'inherit_practice' | 'inherit_global' | 'fixed_en';
  practiceActionButtonLanguages: Record<string, 'inherit' | 'zh-CN' | 'en-US'>;
  practiceActionUiLanguageIndependent?: boolean | null;
  answerAreaChineseUiEnabled: boolean;
  studentSiteTitle: string;
  studentLogoHeightPx: number;
  prototypeSidebarLogoUrl?: string | null;
  showQuestionSets: boolean;
  showLobbyReviewPreferences: boolean;
  showLobbyProgressCard: boolean;
  practiceEntryCheckEnabled: boolean;
  practiceEntryCheckReuseSession: boolean;
  defaultPracticeMode: 'practice' | 'mock';
  showPracticeModeSwitch: boolean;
  allowPracticeModeSwitch: boolean;
  showQuestionDifficulty: boolean;
  showHighFrequencyQuestionBadge: boolean;
  showHighFrequencyProgress: boolean;
  showHighFrequencyNavigationBadge: boolean;
  showNewQuestionBadge: boolean;
  newQuestionWindowDays: number;
  questionBadgeStyle: 'soft' | 'outline';
  showQuestionNavigation: boolean;
  practiceNavigationExpansion: 'question' | 'type' | 'section';
  practiceSidebarCollapsible: boolean;
  practiceSidebarCollapsed: boolean;
  showSidebarNavigationHeader: boolean;
  practiceSidebarWidthPx: number;
  sidebarTypeItemHeightPx: number;
  sidebarTypeFontSizePx: number;
  sidebarTypeItemGapPx: number;
  showSidebarSectionBorder: boolean;
  showSidebarTypeBorder: boolean;
  showSidebarQuestionBorder: boolean;
  showSidebarTypeSelectedColor: boolean;
  showPracticePromptBackground: boolean;
  practicePromptFontStyle: 'normal' | 'bold' | 'italic' | 'bold_italic';
  allowPracticeCopy: boolean;
  showPracticeScrollCue: boolean;
  showPracticeBackToHub: boolean;
  showPracticeTopBackToHub: boolean;
  showPracticeBottomBackToHub: boolean;
  showPracticeTopInfoBar: boolean;
  showPracticeHeaderTimer: boolean;
  showPracticeUserAvatar: boolean;
  showPracticeAvatarPopover: boolean;
  showPracticeAvatarStats: boolean;
  showPracticeAvatarProfileLink: boolean;
  showLogoutInAvatarPopover: boolean;
  showThemeToggle: boolean;
  learningReadingModeEnabled?: boolean;
  learningPresentationModeEnabled?: boolean;
  learningDefaultViewMode?: 'auto' | 'reading' | 'presentation';
  preferEmailAvatar: boolean;
  practiceWatermarkEnabled: boolean;
  practiceWatermarkPosition: 'inline_end' | 'top_right' | 'top_left' | 'bottom_right' | 'bottom_left';
  practiceWatermarkContentMode:
    | 'masked_phone_and_time'
    | 'masked_phone_only'
    | 'display_name_and_time'
    | 'display_name_only'
    | 'student_id_and_time'
    | 'student_id_only'
    | 'time_only';
  promptAudioVolumePercent: number;
  recordingPlaybackVolumePercent: number;
  practiceLeavePromptEnabled: boolean;
  practiceLeavePromptDelaySeconds: number;
  mockLeaveGraceSeconds: number;
  pauseMediaOnHide: boolean;
  stopMediaOnExpire: boolean;
  recordLeaveEvents: boolean;
  contactWechatEnabled: boolean;
  contactDisplayMode: 'single_qr' | 'social_icons' | 'single_qr_social_icons';
  contactWechatTitle?: string | null;
  contactWechatHint?: string | null;
  contactWechatQrUrl?: string | null;
  contactWechatId?: string | null;
  contactWechatAddress?: string | null;
  contactEmail?: string | null;
  contactCopyText?: string | null;
  contactExternalUrl?: string | null;
  contactExternalLabel?: string | null;
  contactWechatIconUrl?: string | null;
  contactExternalIconUrl?: string | null;
  contactEmailIconUrl?: string | null;
  contactCustomEnabled: boolean;
  contactCustomIconUrl?: string | null;
  contactCustomTooltip?: string | null;
  contactCustomActionMode: 'link' | 'dialog';
  contactCustomLinkUrl?: string | null;
  contactCustomDialogTitle?: string | null;
  contactCustomDialogText?: string | null;
  contactCustomDialogButtonLabel?: string | null;
  contactCustomDialogButtonUrl?: string | null;
  logoutIconUrl?: string | null;
  studentFooterCopyright?: string | null;
  themePreset: 'default' | 'forest' | 'amber' | 'high_contrast' | 'archive_home' | 'custom';
  themeTokens: Record<string, string>;
  practiceColorMode: 'inherit_global' | 'use_practice_accent';
}

export interface StudentAnnouncement {
  id: string;
  title: string;
  summary?: string | null;
  body: string;
  kind: 'announcement' | 'maintenance' | 'update' | 'question_type';
  severity: 'info' | 'success' | 'warning' | 'critical';
  displayMode: 'login' | 'login_once' | 'immediate';
  dismissible: boolean;
  actionLabel?: string | null;
  actionUrl?: string | null;
  questionTypeCodes: string[];
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface EmailVerificationCodeResponse {
  cooldownSeconds: number;
  expiresInSeconds: number;
  devCode?: string | null;
}

export interface PasskeyCredentialSummary {
  id: string;
  displayName: string;
  createdAt: string;
  lastUsedAt?: string | null;
  backedUp: boolean;
  backupEligible: boolean;
  transports: string[];
}

export const authApi = {
  createCaptcha: () => apiClient.post<CaptchaChallenge>('/captcha/challenges'),
  sendSmsCode: (payload: {
    phoneCountryCode: string;
    phoneNumber: string;
    captchaChallengeId: string;
    captchaTicket: string;
    scene?: string;
    deviceFingerprint?: string;
  }) => apiClient.post<SmsCodeResponse>('/auth/sms-codes', payload),
  phoneLogin: (payload: {
    phoneCountryCode: string;
    phoneNumber: string;
    code: string;
    scene?: string;
    deviceFingerprint?: string;
  }) => apiClient.post<StudentLoginResponse>('/auth/phone-login', payload),
  sendEmailCode: (payload: {
    email: string;
    captchaChallengeId: string;
    captchaTicket: string;
    scene?: string;
    deviceFingerprint?: string;
  }) => apiClient.post<EmailVerificationCodeResponse>('/auth/email-codes', payload),
  emailLogin: (payload: {
    email: string;
    code: string;
    scene?: string;
    deviceFingerprint?: string;
  }) => apiClient.post<StudentLoginResponse>('/auth/email-login', payload),
  passwordLogin: (payload: {
    loginMethod: 'phone' | 'email';
    phoneCountryCode?: string;
    phoneNumber?: string;
    email?: string;
    password: string;
    captchaChallengeId: string;
    captchaTicket: string;
    deviceFingerprint?: string;
  }) => apiClient.post<StudentLoginResponse>('/auth/password-login', payload),
  passwordRegister: (payload: {
    loginMethod: 'phone' | 'email';
    phoneCountryCode?: string;
    phoneNumber?: string;
    email?: string;
    password: string;
    captchaChallengeId: string;
    captchaTicket: string;
    deviceFingerprint?: string;
    displayName?: string | null;
    contactEmail?: string | null;
    targetScore?: number | null;
    timezone?: string | null;
    inviteCode?: string | null;
    emailVerificationCode?: string | null;
  }) => apiClient.post<StudentLoginResponse>('/auth/password-register', payload),
  passkeyLogin: async () => {
    if (!navigator.credentials || !window.PublicKeyCredential) {
      throw new Error('当前浏览器不支持通行密钥');
    }
    const options = await apiClient.post<Record<string, unknown>>('/auth/passkeys/authentication/options', {});
    const credential = await navigator.credentials.get({ publicKey: normalizePasskeyRequestOptions(options) });
    if (!credential) {
      throw new Error('未完成通行密钥验证');
    }
    return apiClient.post<StudentLoginResponse>('/auth/passkeys/authentication/verify', {
      credential: serializePasskeyCredential(credential),
    });
  },
  listPasskeys: (init?: RequestInit) => apiClient.get<{ items: PasskeyCredentialSummary[] }>('/me/passkeys', init),
  registerPasskey: async (displayName?: string) => {
    if (!navigator.credentials || !window.PublicKeyCredential) throw new Error('当前浏览器不支持通行密钥');
    const options = await apiClient.post<Record<string, unknown>>('/auth/passkeys/registration/options', {});
    const credential = await navigator.credentials.create({ publicKey: normalizePasskeyCreationOptions(options) });
    if (!credential) throw new Error('未完成通行密钥注册');
    return apiClient.post<PasskeyCredentialSummary>('/auth/passkeys/registration/verify', {
      credential: serializePasskeyCredential(credential),
      displayName: displayName?.trim() || null,
    });
  },
  revokePasskey: (credentialId: string) => apiClient.delete<{ revoked: boolean }>(`/me/passkeys/${encodeURIComponent(credentialId)}`),
  logout: () => apiClient.post<{ revokedSessionId: string }>('/auth/logout'),
  getRegistrationSettings: (init?: RequestInit) => apiClient.get<PublicRegistrationSettings>('/auth/registration-settings', init),
  me: (init?: RequestInit) => apiClient.get<{ id: string; phoneCountryCode: string; status: string }>('/me', init),
  getProfile: (init?: RequestInit) => apiClient.get<StudentProfile>('/me/profile', init),
  getShellSettings: (init?: RequestInit) => apiClient.get<StudentShellSettings>('/me/shell-settings', init),
  getActiveAnnouncements: (questionTypeCode?: string, init?: RequestInit) => {
    const suffix = questionTypeCode ? `?questionTypeCode=${encodeURIComponent(questionTypeCode)}` : '';
    return apiClient.get<StudentAnnouncement[]>(`/announcements/active${suffix}`, init);
  },
  dismissAnnouncement: (announcementId: string, dismissed = true) =>
    apiClient.post<StudentAnnouncement>(`/announcements/${announcementId}/dismiss`, { dismissed }),
  updateProfile: (payload: {
    displayName?: string | null;
    contactEmail?: string | null;
    targetScore?: number | null;
    timezone?: string | null;
    preferredPracticeMode?: 'practice' | 'mock' | null;
    inviteCode?: string | null;
    emailVerificationCode?: string | null;
  }) => apiClient.put<StudentProfile>('/me/profile', payload),
  sendEmailVerificationCode: (payload: { email: string }) =>
    apiClient.post<EmailVerificationCodeResponse>('/me/email-verification-codes', payload),
};
